/**
 * Footer — shared across all authenticated routes via Layout.jsx and on
 * standalone marketing pages. Soft-launch wording: no claims about partners
 * that have not been signed; commerce is "coming soon".
 */
import { Link } from 'react-router-dom';
import { Mail, Heart, Handshake } from 'lucide-react';
import BrandLogo from './BrandLogo';

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      className="mt-20 border-t border-white/5 bg-[#050605]"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand column */}
          <div className="md:col-span-5">
            <BrandLogo
              variant="full"
              size="md"
              testId="footer-logo"
              className="mb-4"
            />
            <p className="text-sm text-[#8a948a] leading-relaxed max-w-md">
              The community-built platform for UK carp anglers — catch logs,
              leagues, competitions and lake intel. Modernising carp fishing,
              one session at a time.
            </p>
          </div>

          {/* Navigation column */}
          <div className="md:col-span-3">
            <p className="data-label text-[#c8b273] mb-4">Platform</p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-about">About</Link></li>
              <li><Link to="/lakes" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-lakes">Lake Discovery</Link></li>
              <li><Link to="/competitions" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-competitions">Competitions</Link></li>
              <li><Link to="/learn" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-learn">Learn</Link></li>
              <li><Link to="/subscription" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-membership">Memberships</Link></li>
            </ul>
          </div>

          {/* Support column */}
          <div className="md:col-span-4">
            <p className="data-label text-[#c8b273] mb-4">Support &amp; Legal</p>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/contact" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/privacy" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-[#a8b0a8] hover:text-white transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li>
                <a
                  href="mailto:support@thecarpcast.co.uk"
                  className="inline-flex items-center gap-2 text-[#a8b0a8] hover:text-white transition-colors"
                  data-testid="footer-support-email"
                >
                  <Mail className="w-3.5 h-3.5" />
                  support@thecarpcast.co.uk
                </a>
              </li>
              <li>
                <a
                  href="mailto:partnerships@thecarpcast.co.uk"
                  className="inline-flex items-center gap-2 text-[#a8b0a8] hover:text-[#c8b273] transition-colors"
                  data-testid="footer-partnerships-email"
                >
                  <Handshake className="w-3.5 h-3.5" />
                  partnerships@thecarpcast.co.uk
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-[#6b7d6b]">
            © {year} The Carp Cast. All rights reserved.
          </p>
          <p className="text-xs text-[#6b7d6b] inline-flex items-center gap-1.5">
            Built by anglers, for anglers
            <Heart className="w-3 h-3 text-[#c8b273] fill-[#c8b273]" />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
